import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { getPlantillasProcedimiento, cambiarPlantillaLote } from '../../../api/common';
import { Alertassw } from '../../Alertas/Alertassw';

/**
 * Modal para cambiar la plantilla de un LoteCalibracion YA confirmado ('en_progreso').
 * Componente NUEVO e independiente — NO reutiliza StepSeleccionPlantilla (514 líneas
 * acopladas al flujo de creación; reusarlo sería alto riesgo de regresión).
 *
 * Spec: openspec/changes/lotes-tabs-refresh-plantilla/specs/lotes-cambio-plantilla-post-confirmacion
 *
 * Props:
 *   show                 boolean
 *   onHide               () => void
 *   loteId               string — _id del LoteCalibracion a cambiar
 *   tieneMediciones      boolean — si el lote tiene mediciones registradas (calc en padre)
 *   plantillaActualId    string opcional — _id de la plantilla actual (para excluirla del select)
 *   onPlantillaCambiada  () => void — callback tras éxito (recarga en el padre)
 */
const ModalCambiarPlantilla = ({
    show,
    onHide,
    loteId,
    tieneMediciones = false,
    plantillaActualId,
    onPlantillaCambiada,
}) => {
    const [plantillas, setPlantillas] = useState([]);
    const [loadingPlantillas, setLoadingPlantillas] = useState(false);
    const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState('');
    const [reemplazarMediciones, setReemplazarMediciones] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [errorApi, setErrorApi] = useState(null);
    const [checkboxAutoMarcado, setCheckboxAutoMarcado] = useState(false);

    // Reset y carga al abrir
    useEffect(() => {
        if (!show) return;
        setPlantillaSeleccionadaId('');
        setReemplazarMediciones(false);
        setErrorApi(null);
        setCheckboxAutoMarcado(false);

        const cargar = async () => {
            setLoadingPlantillas(true);
            try {
                const res = await getPlantillasProcedimiento();
                const payload = res?.data?.data ?? res?.data ?? [];
                setPlantillas(
                    Array.isArray(payload)
                        ? payload.filter((p) => p.activo !== false)
                        : []
                );
            } catch (err) {
                console.error(err);
                setErrorApi('No se pudieron cargar las plantillas disponibles.');
            } finally {
                setLoadingPlantillas(false);
            }
        };
        cargar();
    }, [show]);

    const handleConfirmar = async () => {
        if (!plantillaSeleccionadaId) return;

        const confirm = await Swal.fire({
            title: '¿Cambiar plantilla del lote?',
            text: tieneMediciones
                ? 'Este lote tiene mediciones registradas. Se ARCHIVARÁN en el historial y las sesiones se regenerarán desde la nueva plantilla.'
                : 'Se regenerará la estructura de sesiones desde la nueva plantilla.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, cambiar plantilla',
            cancelButtonText: 'Cancelar',
        });
        if (!confirm.isConfirmed) return;

        setGuardando(true);
        setErrorApi(null);
        try {
            await cambiarPlantillaLote(loteId, {
                plantillaId: plantillaSeleccionadaId,
                reemplazarMediciones,
            });
            Alertassw('success', 'Plantilla cambiada correctamente');
            onHide();
            if (onPlantillaCambiada) onPlantillaCambiada();
        } catch (err) {
            // Manejo específico de 422 MEDICIONES_EXISTENTES (spec R3) — auto-marcar checkbox
            // como UX defensiva para que el 2° intento no requiera recordar al usuario.
            const status = err?.response?.status;
            const codigo = err?.response?.data?.error;
            const msg = err?.response?.data?.message;
            if (status === 422 && codigo === 'MEDICIONES_EXISTENTES') {
                setErrorApi(
                    msg ||
                        'El lote tiene mediciones registradas. Marca la casilla para archivarlas y reemplazar la plantilla.'
                );
                setReemplazarMediciones(true);
                setCheckboxAutoMarcado(true);
            } else if (status === 400 && codigo === 'ESTADO_INVALIDO') {
                setErrorApi(msg || 'El lote no está en un estado que permita cambiar la plantilla.');
            } else if (status === 400 && codigo === 'PLANTILLA_INACTIVA') {
                setErrorApi(msg || 'La plantilla seleccionada está inactiva.');
            } else if (status === 400 && codigo === 'PLANTILLA_IGUAL') {
                setErrorApi(msg || 'El lote ya usa esta plantilla — selecciona otra.');
            } else if (status === 404) {
                setErrorApi(msg || 'No se encontró el lote o la plantilla.');
            } else {
                console.error(err);
                setErrorApi(
                    msg || 'Error inesperado al cambiar la plantilla. Intenta nuevamente.'
                );
            }
        } finally {
            setGuardando(false);
        }
    };

    const disabledConfirmar =
        guardando ||
        !plantillaSeleccionadaId ||
        (tieneMediciones && !reemplazarMediciones);

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop={guardando ? 'static' : true}>
            <Modal.Header closeButton={!guardando}>
                <Modal.Title>Cambiar plantilla del lote</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorApi && <Alert variant="danger">{errorApi}</Alert>}

                {loadingPlantillas ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2 text-muted">Cargando plantillas…</p>
                    </div>
                ) : plantillas.length === 0 ? (
                    <Alert variant="info">No hay plantillas activas disponibles.</Alert>
                ) : (
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Nueva plantilla</Form.Label>
                        <Form.Select
                            value={plantillaSeleccionadaId}
                            onChange={(e) => setPlantillaSeleccionadaId(e.target.value)}
                            disabled={guardando}
                            size="lg"
                        >
                            <option value="">-- Selecciona una plantilla --</option>
                            {plantillas
                                .filter((p) => !plantillaActualId || p._id !== plantillaActualId)
                                .map((p) => (
                                    <option key={p._id} value={p._id}>
                                        {p.codigo} - {p.descripcion || p.nombre}
                                    </option>
                                ))}
                        </Form.Select>
                        {plantillaActualId && (
                            <Form.Text className="text-muted">
                                La plantilla actual del lote está excluida del listado.
                            </Form.Text>
                        )}
                    </Form.Group>
                )}

                {tieneMediciones && (
                    <Alert variant="warning">
                        <strong>Atención:</strong> este lote ya tiene mediciones registradas.
                        Al confirmar, se archivarán en el historial
                        (<code>historialMediciones</code>) y no se perderán, pero las
                        sesiones actuales se regenerarán desde la nueva plantilla.
                        <Form.Check
                            className="mt-2"
                            type="checkbox"
                            id="chk-reemplazar-mediciones"
                            label="Reemplazar mediciones existentes (se archivarán en historial)"
                            checked={reemplazarMediciones}
                            onChange={(e) => setReemplazarMediciones(e.target.checked)}
                            disabled={guardando}
                        />
                        {checkboxAutoMarcado && (
                            <div className="small text-muted mt-1">
                                Marcamos la casilla automáticamente tras tu intento anterior —
                                confirma de nuevo para continuar.
                            </div>
                        )}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleConfirmar} disabled={disabledConfirmar}>
                    {guardando ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Guardando…
                        </>
                    ) : (
                        'Confirmar cambio'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalCambiarPlantilla;
